import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  subDays,
  eachDayOfInterval,
  parseISO,
} from 'date-fns';
import {
  Search,
  Download,
  FileSpreadsheet,
  Filter,
  Calendar,
} from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { reportService } from '../../services/reportService';
import { PROGRAM_OPTIONS } from '../../utils/constants';
import { flattenReportData, exportToCSV, exportToExcel } from '../../utils/exportUtils';
import { ReportGrid } from './ReportGrid';
import type { ReportParams, ReportUser, DateRangePreset } from '../../types/report';

const PAGE_SIZE = 20;

/** Compute start/end dates for a preset */
function getPresetDates(preset: DateRangePreset): { start: string; end: string } {
  const today = new Date();
  switch (preset) {
    case 'today':
      return { start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    case 'yesterday': {
      const d = subDays(today, 1);
      return { start: format(d, 'yyyy-MM-dd'), end: format(d, 'yyyy-MM-dd') };
    }
    case 'thisWeek':
      return {
        start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      };
    case 'lastWeek': {
      const prev = subWeeks(today, 1);
      return {
        start: format(startOfWeek(prev, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(prev, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    }
    case 'thisMonth':
      return {
        start: format(startOfMonth(today), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      };
    case 'lastMonth': {
      const prev = subMonths(today, 1);
      return {
        start: format(startOfMonth(prev), 'yyyy-MM-dd'),
        end: format(endOfMonth(prev), 'yyyy-MM-dd'),
      };
    }
    default:
      return { start: '', end: '' };
  }
}

const PRESET_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

const PRESENCE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'present', label: 'Present Only' },
  { value: 'absent', label: 'Absent Only' },
];

export const LocationReports = () => {
  // Date range state
  const [preset, setPreset] = useState<DateRangePreset>('thisWeek');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Filter state
  const [selectedProgram, setSelectedProgram] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [presenceFilter, setPresenceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { showError } = useNotification();

  // Resolved date range
  const { startDate, endDate } = useMemo(() => {
    if (preset === 'custom') {
      return { startDate: customStart, endDate: customEnd };
    }
    const { start, end } = getPresetDates(preset);
    return { startDate: start, endDate: end };
  }, [preset, customStart, customEnd]);

  // Date array for grid columns
  const dates = useMemo(() => {
    if (!startDate || !endDate) return [];
    try {
      return eachDayOfInterval({
        start: parseISO(startDate),
        end: parseISO(endDate),
      });
    } catch {
      return [];
    }
  }, [startDate, endDate]);

  // Build API params
  const params: ReportParams | null = useMemo(() => {
    if (!startDate || !endDate) return null;
    const p: ReportParams = {
      start_date: startDate,
      end_date: endDate,
      page: currentPage,
      page_size: PAGE_SIZE,
      sort_by: 'name',
      sort_order: 'asc',
    };
    if (selectedProgram) p.program_id = selectedProgram;
    if (searchQuery.trim()) p.name = searchQuery.trim();
    if (presenceFilter === 'present') p.at_least_present_once = true;
    if (presenceFilter === 'absent') p.not_present_in_interval = true;
    return p;
  }, [startDate, endDate, currentPage, selectedProgram, searchQuery, presenceFilter]);

  // Fetch report
  const {
    data: reportData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['location-report', params],
    queryFn: () => reportService.getReport(params!),
    enabled: params !== null,
    placeholderData: (prev) => prev,
  });

  const users = reportData?.data ?? [];
  const totalUsers = reportData?.total ?? 0;
  const totalPages = Math.ceil(totalUsers / PAGE_SIZE);

  // --- Filter handlers ---
  const handlePresetChange = (value: DateRangePreset) => {
    setPreset(value);
    setCurrentPage(1);
  };

  const handleProgramChange = (value: string) => {
    setSelectedProgram(value);
    setCurrentPage(1);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePresenceChange = (value: string) => {
    setPresenceFilter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedProgram('');
    setSearchQuery('');
    setPresenceFilter('all');
    setPreset('thisWeek');
    setCustomStart('');
    setCustomEnd('');
    setCurrentPage(1);
  };

  // --- Export handlers ---
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(
    async (type: 'csv' | 'excel') => {
      if (dates.length === 0 || !startDate || !endDate) {
        showError('No Data', 'No data available to export.');
        return;
      }

      try {
        setExporting(true);

        // Fetch ALL pages for export
        const allUsers: ReportUser[] = [];
        let page = 1;
        const exportPageSize = 100;
        let hasMore = true;

        while (hasMore) {
          const exportParams: ReportParams = {
            start_date: startDate,
            end_date: endDate,
            page,
            page_size: exportPageSize,
            sort_by: 'name',
            sort_order: 'asc',
            // Support team explicitly wants inactive users out of the
            // export — pinning status=Active on the download only, so
            // the on-screen report still shows every configured user.
            status: 'Active',
          };
          if (selectedProgram) exportParams.program_id = selectedProgram;
          if (searchQuery.trim()) exportParams.name = searchQuery.trim();
          if (presenceFilter === 'present') exportParams.at_least_present_once = true;
          if (presenceFilter === 'absent') exportParams.not_present_in_interval = true;

          const result = await reportService.getReport(exportParams);
          allUsers.push(...result.data);
          hasMore = allUsers.length < result.total;
          page++;
        }

        if (allUsers.length === 0) {
          showError('No Data', 'No data available to export.');
          return;
        }

        const { headers, rows } = flattenReportData(allUsers, dates);
        const dateSuffix = `${startDate}_to_${endDate}`;
        if (type === 'csv') {
          exportToCSV(`location_report_${dateSuffix}.csv`, headers, rows);
        } else {
          exportToExcel(`location_report_${dateSuffix}.xlsx`, headers, rows);
        }
      } catch (err) {
        console.error('Export failed:', err);
        showError('Export Failed', 'Failed to export report data. Please try again.');
      } finally {
        setExporting(false);
      }
    },
    [dates, startDate, endDate, selectedProgram, searchQuery, presenceFilter, showError]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-rgf-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-rgf-gray-900">Location Tracking Reports</h1>
            <p className="text-sm text-rgf-gray-500">
              Attendance and activity reports for location-tracked users.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon={<Download className="h-4 w-4" />}
              onClick={() => handleExport('csv')}
              disabled={exporting || !startDate || !endDate}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<FileSpreadsheet className="h-4 w-4" />}
              onClick={() => handleExport('excel')}
              disabled={exporting || !startDate || !endDate}
            >
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="px-6 py-4 bg-white border-b border-rgf-gray-200">
        <div className="space-y-3">
          {/* Row 1: Date range + search */}
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-end">
            {/* Date Preset */}
            <div>
              <label className="block text-xs font-medium text-rgf-gray-600 mb-1">
                <Calendar className="h-3 w-3 inline mr-1" />
                Date Range
              </label>
              <select
                value={preset}
                onChange={(e) => handlePresetChange(e.target.value as DateRangePreset)}
                className="px-3 py-2 border border-rgf-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              >
                {PRESET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom date pickers */}
            {preset === 'custom' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-rgf-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => {
                      setCustomStart(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-rgf-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-rgf-gray-600 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => {
                      setCustomEnd(e.target.value);
                      setCurrentPage(1);
                    }}
                    min={customStart}
                    className="px-3 py-2 border border-rgf-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>
              </>
            )}

            {/* Date range display */}
            {startDate && endDate && preset !== 'custom' && (
              <div className="text-sm text-rgf-gray-600 py-2">
                {format(parseISO(startDate), 'MMM dd')} — {format(parseISO(endDate), 'MMM dd, yyyy')}
                <span className="text-rgf-gray-400 ml-2">({dates.length} days)</span>
              </div>
            )}

            <div className="flex-1" />

            {/* Search */}
            <div className="w-full lg:w-64">
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                startIcon={<Search className="h-4 w-4" />}
              />
            </div>

            <Button
              variant="secondary"
              size="sm"
              icon={<Filter className="h-4 w-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Less' : 'More'}
            </Button>
          </div>

          {/* Row 2: Additional filters (collapsible) */}
          {showFilters && (
            <div className="flex flex-wrap gap-3 pt-3 border-t border-rgf-gray-200">
              {/* Program Filter */}
              <div>
                <label className="block text-xs font-medium text-rgf-gray-600 mb-1">
                  Program
                </label>
                <select
                  value={selectedProgram}
                  onChange={(e) => handleProgramChange(e.target.value)}
                  className="px-3 py-2 border border-rgf-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                >
                  <option value="">All Programs</option>
                  {PROGRAM_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Presence Filter */}
              <div>
                <label className="block text-xs font-medium text-rgf-gray-600 mb-1">
                  Attendance
                </label>
                <select
                  value={presenceFilter}
                  onChange={(e) => handlePresenceChange(e.target.value)}
                  className="px-3 py-2 border border-rgf-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                >
                  {PRESENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear All */}
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="px-6 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {(error as any)?.response?.data?.message ||
              (error as any)?.message ||
              'Failed to load report data.'}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {!startDate || !endDate ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Calendar className="h-12 w-12 text-rgf-gray-300 mx-auto mb-3" />
              <p className="text-rgf-gray-500">Select a date range to view reports.</p>
            </div>
          </div>
        ) : (
          <ReportGrid users={users} dates={dates} isLoading={isLoading} />
        )}
      </div>

      {/* Pagination */}
      {totalUsers > 0 && (
        <div className="px-6 py-3 bg-white border-t border-rgf-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-rgf-gray-700">
              Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
              {Math.min(currentPage * PAGE_SIZE, totalUsers)} of {totalUsers} users
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-rgf-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
